import { Box, Stack, Typography, useTheme } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import type { FC } from "react";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { useGoalDetailComposition } from "web_myplann/Goals/Hooks/useGoalDetailComposition";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";

type Props = {
	goal: GoalDetail;
};

export const GoalDetailComposition: FC<Props> = ({ goal }) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();
	const composition = useGoalDetailComposition(goal);

	if (!composition) return null;

	return (
		<CollapsibleSection title={t(Resources.Goals.Detail.Composition.title)}>
			<Stack
				bgcolor={theme.palette.background.paper}
				px={theme.spacingTokens.lg}
				pt={theme.spacingTokens.xl}
				pb={theme.spacingTokens.xl}
				alignItems="center"
			>
				<Box width="80%" maxWidth={240} height={240} position="relative">
					<PieChart
						series={[
							{
								data: composition.slices.map((d) => ({
									id: d.id,
									value: d.value,
									label: d.label,
									color: d.color,
								})),
								innerRadius: 56,
								outerRadius: 92,
								paddingAngle: 3,
								cornerRadius: 4,
							},
						]}
						hideLegend
						height={240}
					/>
					<Stack
						position="absolute"
						alignItems="center"
						justifyContent="center"
						sx={{ inset: 0, pointerEvents: "none" }}
					>
						<Typography
							variant="h1"
							color={theme.palette.primary.main}
							tabular
							lineHeight={1}
						>
							{composition.coveredPercent}%
						</Typography>
						<Typography variant="hint" mt={theme.spacingTokens.xs}>
							{t(Resources.Goals.Detail.Composition.coveredLabel)}
						</Typography>
					</Stack>
				</Box>
				<Stack
					gap={theme.spacingTokens.sm}
					width="100%"
					mt={theme.spacingTokens.lg}
				>
					{composition.slices.map((item) => (
						<Stack key={item.id} direction="row" alignItems="center" gap={1}>
							<Box
								width={10}
								height={10}
								borderRadius="50%"
								bgcolor={item.color}
							/>
							<Typography
								variant="caption"
								color={theme.palette.grey[700]}
								flex={1}
							>
								{item.label}
							</Typography>
							<Typography
								variant="button"
								color={theme.palette.grey[900]}
								tabular
							>
								{formatAmount(item.value, goal.currencyCode)}
							</Typography>
						</Stack>
					))}
				</Stack>
			</Stack>
		</CollapsibleSection>
	);
};
